import {Flag, Tooltip, TooltipProps} from 'platform/components';
import {HStack} from 'platform/foundation';

import {forwardRef, useMemo} from 'react';

import {is, isNil} from 'ramda';

import {ICellRenderer, ICellRendererParams} from '../../types/AgGridTypes';
import {FlagPropsGetter} from '../../types/FlagPropsGetter';
import {TooltipGetter} from '../../types/TooltipGetter';
import {getFlagTooltipLabel} from './utils/getFlagTooltipLabel';

export interface FlagCellRendererProps extends ICellRendererParams {
  getTooltipProps?: TooltipGetter;
  getFlagProps?: FlagPropsGetter;
}

const isArray = is(Array);

function FlagCellRendererComponent(
  {getValue, value, getTooltipProps, data, node, getFlagProps}: FlagCellRendererProps,
  _ref: React.Ref<ICellRenderer>
) {
  const cellValue = getValue?.() ?? value;

  const tooltipProps = useMemo<TooltipProps>(
    () => (getTooltipProps ? getTooltipProps({data, node}) : ({} as TooltipProps)),
    [data, getTooltipProps, node]
  );

  const flagRendererProps = useMemo(() => {
    if (getFlagProps && data) {
      return getFlagProps({data});
    }
    return {};
  }, [getFlagProps, data]);

  // @ts-ignore
  if (isNil(cellValue?.value)) {
    return null;
  }

  // @ts-ignore
  if (!isArray(cellValue?.value)) {
    // @ts-ignore
    const flagColor = cellValue?.style?.flagColor;
    // @ts-ignore
    const flagIsSubtle = cellValue?.style?.flagSubtle;
    // @ts-ignore
    const flagLabel = cellValue?.value?.label;

    return (
      <Tooltip {...tooltipProps}>
        {cellValue && (
          <Flag
            {...flagRendererProps}
            isSubtle={flagIsSubtle}
            colorScheme={flagColor}
            label={flagLabel}
          />
        )}
      </Tooltip>
    );
  }

  return (
    <HStack spacing={2}>
      {/* @ts-ignore */}
      {cellValue?.value?.map((cellValueItem, index) => {
        // @ts-ignore
        const flagColor = cellValue?.style?.[index]?.flagColor;
        // @ts-ignore
        const flagIsSubtle = cellValue?.style?.[index]?.flagSubtle;
        const flagLabel = cellValueItem?.label;

        return (
          cellValueItem && (
            <Tooltip
              label={getFlagTooltipLabel(cellValue?.value, cellValueItem?.label)}
              isDisabled={tooltipProps.isDisabled}
              openDelay={500}
              overflowWrap={tooltipProps.overflowWrap}
              hasAutoWidth
            >
              <Flag
                key={`${cellValueItem?.label}-${index}`}
                {...flagRendererProps}
                isSubtle={flagIsSubtle}
                colorScheme={flagColor}
                label={flagLabel}
              />
            </Tooltip>
          )
        );
      })}
    </HStack>
  );
}

export const FlagCellRenderer = forwardRef<ICellRenderer, FlagCellRendererProps>(
  FlagCellRendererComponent
);
